/**
 * Every API path the web app calls, in one place. The API contract is
 * docs/sprints/WMS-API.md; when a path or shape there changes, change it here (and the type
 * in ./types.ts) only. The /api/v1 prefix is added by api(). The tenant comes from the session —
 * the client never sends a tenantId for scoping.
 */
import type { Page } from './api';
import type { Client, Device, HomeSummary, LoginResponse, Notification, ParcelDetail, ParcelRow, ReasonCode, Resolution, Route, SessionResponse, Site, TimelineRow } from './types';

const id = (x: string) => encodeURIComponent(x);

export const EP = {
  auth: {
    login: '/auth/login',
    mfa: '/auth/mfa',
    mfaSetup: '/auth/mfa/setup',
    mfaEnable: '/auth/mfa/enable',
    password: '/auth/password',
    refresh: '/auth/refresh',
    logout: '/auth/logout',
    me: '/auth/me',
    reissue: '/auth/reissue',
    /** Switch the session's tenant (body { orgId }) → SessionResponse. */
    switchOrg: '/auth/org',
  },
  settingsPublic: '/settings/public',
  settings: '/settings',
  notifications: '/notifications',
  notificationsRead: '/notifications/read',
  usersDirectory: '/users/directory',
  files: '/files',
  file: (fileId: string) => `/files/${id(fileId)}`,
  // Setup (FR-SET-01..06)
  sites: '/sites',
  site: (siteId: string) => `/sites/${id(siteId)}`,
  locations: '/locations',
  locationsTree: '/locations/tree',
  locationsGenerate: '/locations/generate',
  locationLabels: '/locations/labels',
  location: (locId: string) => `/locations/${id(locId)}`,
  clients: '/clients',
  client: (clientId: string) => `/clients/${id(clientId)}`,
  routes: '/routes',
  routesMatch: '/routes/match',
  route: (routeId: string) => `/routes/${id(routeId)}`,
  reasonCodes: '/reason-codes',
  reasonCode: (rid: string) => `/reason-codes/${id(rid)}`,
  devices: '/devices',
  device: (did: string) => `/devices/${id(did)}`,
  adminSettings: '/admin/settings',
  // Parcels (FR-ID, FR-TRK-02)
  parcels: '/parcels',
  parcel: (pid: string) => `/parcels/${id(pid)}`,
  parcelEvents: (pid: string) => `/parcels/${id(pid)}/events`,
  parcelUnmask: (pid: string) => `/parcels/${id(pid)}/unmask`,
  parcelAliases: (pid: string) => `/parcels/${id(pid)}/aliases`,
  parcelRoute: (pid: string) => `/parcels/${id(pid)}/route`,
  parcelRelabel: (pid: string) => `/parcels/${id(pid)}/relabel`,
  parcelLabel: (pid: string) => `/parcels/${id(pid)}/label`,
  tagsBind: '/tags/bind',
  tagsUnbind: '/tags/unbind',
  resolve: '/resolve',
  scanLookup: '/scan/lookup',
  chainVerify: '/parcel-events/verify',
} as const;

export interface ParcelQuery {
  q?: string;
  siteId?: string;
  clientId?: string;
  status?: string;
  routeId?: string;
  locationId?: string;
  onHold?: boolean;
  hasException?: boolean;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

/** The typed contract: request → response for the calls the shell and W1 screens make. */
export interface Contract {
  'POST /auth/login': { body: { login: string; password: string }; res: LoginResponse };
  'POST /auth/mfa': { body: { mfaToken: string; code: string }; res: SessionResponse };
  'POST /auth/org': { body: { orgId: string | null }; res: SessionResponse };
  'GET /settings/public': { res: { brandName?: string; idleTimeoutMinutes?: number } };
  'GET /users/directory': { res: { id: string; name: string; email?: string }[] };
  'GET /notifications': { res: Page<Notification> };
  'GET /sites': { res: Site[] };
  'GET /clients': { res: Client[] };
  'GET /routes': { res: Route[] };
  'GET /reason-codes': { res: ReasonCode[] };
  'GET /devices': { res: Device[] };
  'GET /parcels': { query: ParcelQuery; res: Page<ParcelRow> & { exact: boolean } };
  'GET /parcels/:id': { res: ParcelDetail };
  'GET /parcels/:id/events': { res: TimelineRow[] };
  'POST /resolve': { body: { codes: string[] }; res: Resolution[] };
  'GET /home': { res: HomeSummary };
}
export type Res<K extends keyof Contract> = Contract[K]['res'];
